import Input from '@components/primitives/Input'
import ModalOverlay from '@components/primitives/Modal/ModalOverlay'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass'
import type { TextColor } from '@lib/colors/colors'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import type { RefinementListConnectorParams } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'
import debounce from 'lodash/debounce'
import type { ChangeEvent, MouseEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useRefinementList } from 'react-instantsearch'
import { RefinementListItem } from './RefinementListItem'

type RefinementListProvidedWithPlaceholder = RefinementListConnectorParams & {
  autoFocus?: boolean
  highlightColor?: TextColor
  inputClassName?: string
  menuPlacement?: 'top'
  onSelect: (value: string) => void
  placeholder?: string
  retainValueOnSelect?: boolean
  trackingEventType?: string
}

/**
 * Condition autocomplete component connected to our global Algolia search index
 */
export default function ConditionSearch(
  props: RefinementListProvidedWithPlaceholder,
) {
  const {
    autoFocus = false,
    highlightColor,
    inputClassName,
    menuPlacement,
    onSelect,
    placeholder = 'Type condition to add...',
    retainValueOnSelect = false,
  } = props
  const { items, searchForItems } = useRefinementList(props)
  const [refinementListPickerVisible, setRefinementListPickerVisible] =
    useState(false)
  const [inputValue, setInputValue] = useState<string>('')

  const inputChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    if (!refinementListPickerVisible) {
      setRefinementListPickerVisible(true)
    }

    setInputValue(event.currentTarget.value)

    if (event.currentTarget.value.length >= 1) {
      searchForItems(event.currentTarget.value)
    } else {
      setRefinementListPickerVisible(false)
    }

    trackEvent(TrackingEvents.TRIAL_PRO_ONBOARDING_CONDITION_SEARCH_UPDATED, {
      type: 'condition',
      val: event.currentTarget.value,
    })
  }
  const debouncedInputHandler = debounce(inputChangeHandler, 250, {
    leading: true,
    trailing: false,
  })

  const selectedItemHandler =
    (pickedFacet: string) => (event: MouseEvent<HTMLLIElement>) => {
      event.preventDefault()
      onSelect(pickedFacet)
      setInputValue(retainValueOnSelect ? pickedFacet : '')
      onClose()
    }

  const onClose = () => setRefinementListPickerVisible(false)

  // Need to focus manually as autoFocus={true} property on input not working.
  const conditionInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (autoFocus && conditionInput.current) {
      conditionInput.current.focus()
    }
  }, [autoFocus])

  return (
    <div className='relative'>
      <Input
        className={classMerge('w-full text-ellipsis text-lg', inputClassName)}
        icon={faMagnifyingGlass}
        innerRef={conditionInput}
        onChange={debouncedInputHandler}
        // If value is retained after selection, clear it next time input is focused
        onFocus={retainValueOnSelect ? () => setInputValue('') : undefined}
        placeholder={placeholder}
        type='text'
        value={inputValue}
      />
      {/* List of items to refine given the input search box above */}
      <ul
        className={classMerge(
          `absolute z-modalSearchPanel mx-auto max-h-60 w-full overflow-y-auto rounded-lg border-2 border-neutral200 bg-white`,
          menuPlacement === 'top' && 'bottom-full',
        )}
        hidden={!refinementListPickerVisible}
      >
        {items.slice(0, 10).map((item, index) => (
          <RefinementListItem
            highlightColor={highlightColor}
            item={item}
            key={index}
            selectedItemHandler={selectedItemHandler}
          />
        ))}
        {items.length === 0 && (
          <Text
            className='px-4 py-2'
            styleName='p-strong'
            value='No matching conditions found'
          />
        )}
      </ul>
      <ModalOverlay hidden={!refinementListPickerVisible} onClick={onClose} />
    </div>
  )
}
